import type {
  CustomCartItem,
  UpsellItem,
} from '@bulknutrients/bulk-shared-lib/interfaces/ICart';
import {
  CLOUDINARY_ASSET_FIELDS,
  IMAGE_FIELDS,
} from '@bulknutrients/bulk-shared-lib/lib/queries/sanity/snippets';
import { cdnClient as client } from '@bulknutrients/bulk-shared-lib/lib/sanity/client';
import { ErrorAlert } from '@components/Alerts/ErrorAlert';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { CartGiftCertificate } from '@components/Cart/CartGiftCertificate';
import { CartItem } from '@components/Cart/CartItem';
import { CartSummary } from '@components/Cart/CartSummary';
import { CartUpsell } from '@components/Cart/CartUpsell';
import { CartWeightIndicator } from '@components/Cart/CartWeightIndicator';
import { EmptyCartMessage } from '@components/Cart/EmptyCartMessage';
import { TailoredCartUpsell } from '@components/Cart/QuickCart/QuickCart';
import { Container } from '@components/Container';
import {
  UIContext,
  type UIContextInterface,
} from '@components/Context/UIContext/UIContext';
import { HorizontalDivider } from '@components/HorizontalDivider/HorizontalDivider';
import { StandardLayout } from '@components/Layouts/StandardLayout';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import { useCart } from '@hooks/cart/useCart';
import type { GetServerSideProps } from 'next';
import {
  type ReactElement,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import useSWR from 'swr';

const LineItems = ({ items }: { items: CustomCartItem[] }): ReactElement => (
  <div>
    {items.map((lineItem: CustomCartItem) => {
      return <CartItem item={lineItem} key={lineItem.id} />;
    })}
  </div>
);

const fetcher = async () => {
  const query = `//groq
  *[_type == "regularCartUpsell" 
    && !(_id in path("drafts.**")) 
    && dateTime(startsAt) < dateTime(now())
    && dateTime(expiresAt) > dateTime(now())
    ][0]
    {
      message,
      "image": select(
        image.asset._type == "cloudinary.asset" => image{
          ${CLOUDINARY_ASSET_FIELDS}
        },
        image._type == "image" => image{
          ${IMAGE_FIELDS}
      }),
      "variant": variant->{
        variantId, 
        sku,
        productId,
      },
    }
  `;

  const result = await client.fetch(query);
  if (!result) {
    const error = new Error('Unable to fetch cart upsell item');
    throw error;
  }

  return result;
};

const CartContent = () => {
  const [mounted, setMounted] = useState(false);

  // periodically get upsell banner
  const { data: upsellItem } = useSWR<UpsellItem>(
    '/api/regularCartUpsell',
    fetcher,
    {
      refreshInterval: 1200000, // 20 minutes
      revalidateOnFocus: false,
    }
  );

  const { quickCartIsOpen, setQuickCartIsOpen } =
    useContext<UIContextInterface>(UIContext); // Just to be able to close quick cart
  const { cart, error: cartError, isLoading } = useCart();
  const [showUpsellBanner, setShowUpsellBanner] = useState(true);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Extract line items from cart (memo no need for async)
  const cartItems = useMemo(() => {
    if (cart?.custom_items) {
      return [...cart.custom_items];
    }
    return [];
  }, [cart?.custom_items]);

  // Extract gift certificates from cart (memo no need for async)
  const giftCertificates = useMemo(() => {
    if (cart?.line_items.gift_certificates) {
      return [...cart.line_items.gift_certificates];
    }
    return [];
  }, [cart]);

  useEffect(() => {
    let mounted = true;

    if (mounted && quickCartIsOpen) setQuickCartIsOpen(false);
    return function cleanup() {
      mounted = false;
    };
  }, [quickCartIsOpen, setQuickCartIsOpen]);

  // Work out if upsell banner should be displayed
  useEffect(() => {
    let mounted = true;
    setShowUpsellBanner(true);
    // 1. empty cart
    if (cartItems.length === 0) {
      if (mounted) setShowUpsellBanner(false);
      return function cleanup() {
        mounted = false;
      };
    }

    // 2. has upsell item in cart already
    if (
      cartItems.findIndex((item) => item.sku === upsellItem?.variant.sku) > -1
    ) {
      if (mounted) setShowUpsellBanner(false);
      return function cleanup() {
        mounted = false;
      };
    }

    // 3. has ebook (BN-EBOOK) only (this is really if nothing physical)
    if (cart?.line_items.physical_items.length === 0) {
      if (mounted) setShowUpsellBanner(false);
    }

    return function cleanup() {
      mounted = false;
    };
  }, [cartItems, cart, upsellItem]);

  // Loading the page or cart still
  if (!mounted || isLoading) {
    return (
      <Container as="main" id="main-content">
        <div className="flex min-h-[300px] flex-col items-center justify-center justify-self-center">
          <h1>Your Cart</h1>
          <div className="flex flex-col gap-4">
            <Paragraph>Loading your cart...</Paragraph>
          </div>
        </div>
      </Container>
    );
  }

  // Loaded, but nothing in cart
  if (cartItems.length === 0 && giftCertificates.length === 0) {
    return (
      <Container as="main" id="main-content">
        <Breadcrumbs
          crumbs={[
            { title: 'Home', path: '/' },
            { title: 'Cart', path: '/cart' },
          ]}
        />
        <div className="mx-auto my-0 w-full justify-self-center md:w-[80%] lg:w-[70%] xl:w-[60%]">
          <h1>Your Cart</h1>
          <HorizontalDivider />
          <EmptyCartMessage />
        </div>
      </Container>
    );
  }

  return (
    <Container as="main" id="main-content">
      <Breadcrumbs
        crumbs={[
          { title: 'Home', path: '/' },
          { title: 'Cart', path: '/cart' },
        ]}
      />
      <div className="mx-auto my-0 w-full justify-self-center md:w-[80%] lg:w-[70%] xl:w-[60%]">
        <h1>Your Cart</h1>
        <CartUpsell
          showUpsellBanner={showUpsellBanner}
          upsellItem={upsellItem}
        />

        <HorizontalDivider className="my-5" />

        {cartError ? (
          <>
            <Paragraph>
              We were unable to retrieve a cart from the server at this time.
            </Paragraph>
            <pre>{cartError.message}</pre>
          </>
        ) : (
          <>
            <LineItems items={cartItems} />

            {giftCertificates.map((cert) => (
              <CartGiftCertificate key={cert.id} certificate={cert} />
            ))}

            <div className="mt-4 flex flex-col gap-2">
              {cart && <CartWeightIndicator weight={cart.weight} />}

              {/* Targeted Upsell */}
              <TailoredCartUpsell />
            </div>

            <CartSummary cart={cart} items={cartItems} />
          </>
        )}
      </div>
    </Container>
  );
};

const CHECKOUT_ERRORS = {
  '5001': (
    <Paragraph>
      There was an error processing your checkout. It appears that you have
      already taken advantage of the free shipping offer with this sample pack.
      If this is a mistake, please{' '}
      <TextLink href="/contact-us">get in touch</TextLink> with our Customer
      Service team. Else continue to checkout without the free shipping offer.
    </Paragraph>
  ),
};

export default function CartPage({ error }: { error?: string }): ReactElement {
  const [isError, setIsError] = useState(!!error);

  return (
    <>
      <ErrorAlert show={isError} setShow={setIsError}>
        {CHECKOUT_ERRORS[error as string]}
      </ErrorAlert>
      <CartContent />
    </>
  );
}

CartPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  if (query.error) {
    return {
      props: {
        error: query.error,
      },
    };
  }

  return {
    props: {},
  };
};
